<template>
  <item-dropdown
    :item="item"
    type="folder"
    @toggle-hidden="toggleHidden"
    @toggle-activity="toggleAgencyActivity"
    @toggle-archived="toggleArchived"
    @delete="deleteFolder"
    @add-sub-folder="openManageFolderModal(item._id)"
    @settings="openManageFolderModal(null, item)"
  />
</template>

<script>
import { defineComponent, inject } from 'vue'
import ItemDropdown from './ItemDropdown.vue'
import { DialogProgrammatic } from '@akerolabs/akero-vue/src/components/dialog'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import { success, error } from '../utils'

export default defineComponent({
  name: 'FolderDropdown',
  components: {
    [ItemDropdown.name]: ItemDropdown,
  },
  props: {
    item: {
      type: Object,
      default: () => ({}),
    },
    children: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update'],
  setup(props, { emit }) {
    const openManageFolderModal = inject('openManageFolderModal')

    const toggleArchived = async () => {
      const archived = props.item.archived ? false : true
      DialogProgrammatic.confirm({
        title: archived ? 'Archive Folder' : 'Unarchive Folder',
        message:
          'This action will apply to all folders/projects in this folder',
        type: 'primary',
        confirmText: 'OK',
        onConfirm: async () => {
          try {
            await request.post(`/campaigns/${props.item._id}/archive`, {
              archived,
              children: props.children,
            })
            success(
              archived
                ? 'Project(s) archived successfully'
                : 'Project(s) unarchived successfully',
            )
            emit('update')
          } catch (ex) {
            error('Failed to update project', ex)
          }
        },
      })
    }

    const toggleHidden = async () => {
      const hidden = props.item.hidden ? false : true
      DialogProgrammatic.confirm({
        title: hidden ? 'Set Natives Only' : 'Unset Natives Only',
        message:
          'This action will apply to all folders/projects in this folder',
        type: 'primary',
        confirmText: 'OK',
        onConfirm: async () => {
          try {
            await request.post(`/campaigns/${props.item._id}/hide`, {
              hidden,
              children: props.children,
            })
            success(
              hidden
                ? 'Project(s) hidden successfully'
                : 'Project(s) unhidden successfully',
            )
            emit('update')
          } catch (ex) {
            error('Failed to update project', ex)
          }
        },
      })
    }

    const toggleAgencyActivity = async () => {
      const agencyActivity = props.item.agencyActivity ? false : true
      DialogProgrammatic.confirm({
        title: agencyActivity
          ? 'Set Natives Activity'
          : 'Unset Natives Activity',
        message:
          'This action will apply to all folders/projects in this folder',
        type: 'primary',
        confirmText: 'OK',
        onConfirm: async () => {
          try {
            await request.post(`/campaigns/${props.item._id}/agencyactivity`, {
              agencyActivity,
              children: props.children,
            })
            success(
              agencyActivity
                ? 'Project(s) set as Natives activity.'
                : 'Project(s) unset as Natives activity.',
            )
            emit('update')
          } catch (ex) {
            error('Failed to update project', ex)
          }
        },
      })
    }

    const deleteFolder = async () => {
      try {
        if (props.children.length > 0) {
          DialogProgrammatic.alert({
            title: 'Delete Folder',
            message: `
              <h4>You cannot delete this folder as it is not empty</h4>
              <p class="mb-none">Please empty this folder first.</p>
            `,
            confirmText: 'Okay',
            type: 'warning',
          })
        } else {
          DialogProgrammatic.confirm({
            title: 'Delete Folder?',
            message: `
              <p>Are you sure you want to delete the "${props.item.title}" folder?</p>
            `,
            confirmText: 'Yes, delete folder',
            type: 'danger',
            onConfirm: async () => {
              await request.delete(`/campaigns/${props.item._id}`)
              emit('update')
              success('Folder removed successfully')
            },
          })
        }
      } catch (ex) {
        error('Failed to remove folder', ex)
      }
    }

    return {
      toggleArchived,
      toggleHidden,
      toggleAgencyActivity,
      deleteFolder,
      openManageFolderModal,
    }
  },
})
</script>
